import { inject, Injectable } from '@angular/core';
import { getStoresSnapshot } from '@ngneat/elf';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable, Subject, filter, map } from 'rxjs';

import { AnalyticsService } from '@digital-platform/rs-analytics/services';
import { User, UserFromClassic } from '@digital-platform/rs-auth/domain';
import { IdentityRepository } from './identity.repository';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class IdentityService {
  public repo: IdentityRepository = inject(IdentityRepository);
  private analyticsService = inject(AnalyticsService); //@AURA
  public state$: Observable<User> = this.repo.state$;

  public hasDotComAccess$: Observable<boolean> = this.state$.pipe(
    map(
      (user: User) =>
        user?.profile?.app_metadata?.user_role === 'CSR' ||
        !!(
          user?.profile?.app_metadata.cdh_contact_profile_id &&
          user?.profile?.app_metadata.cdh_id
        )
    )
  );

  // user exists and has value, sometimes we don't want to check if the user is empty
  public validUser$: Observable<User> = this.state$.pipe(
    filter((user) => Object.keys(user).length !== 0)
  );

  public isLoggedIn = false;
  public isV1LoggedIn = false;
  private userIdentity: User | null = null;
  private _v1LoginComplete$ = new Subject<boolean>();
  public v1LoginComplete$ = this._v1LoginComplete$.asObservable();

  public hasDotComAccess = (user: User) =>
    user?.profile?.app_metadata?.user_role === 'CSR' ||
    !!(
      user?.profile?.app_metadata.cdh_contact_profile_id &&
      user?.profile?.app_metadata.cdh_id
    );

  public login(identity: User): void {
    this.repo.set(identity);
    this.analyticsService.registerLoggedInUserState(
      identity.profile?.sub || '',
      identity.profile?.app_metadata?.cdh_id || ''
    );
  }

  /**
   * Merge the user object from classic with the current user object
   * @param user
   */
  public mergeUser(user: UserFromClassic): void {
    const newUser = {
      ...this.userIdentity,
      ...user,
    };

    if (newUser) {
      this.login(newUser);
    }
  }

  public logout(): void {
    this.repo.reset();
    this.analyticsService.registerLoggedOutUserState();
  }

  constructor() {
    this.state$.subscribe((user) => {
      this.isLoggedIn = user && Object.keys(user).length > 0;
      this.isV1LoggedIn = user && Object.keys(user).length > 1; // loginComplete$ subscription below only sets a single profile key for non-dotcom users
      this.userIdentity = user;

      if (this.isV1LoggedIn) {
        this._v1LoginComplete$.next(true);
      }
    });
  }

  public getStateSnapshot(): User {
    return getStoresSnapshot()[this.repo.store.name];
  }
}
